import { memo, useMemo } from 'react';
import { getImprovementColor, getImprovementDisplay, WellnessMetricDisplay } from './wellnessUtils';
import ProgressBar from './ProgressBar';

interface WellnessChartTooltipProps {
  row: WellnessMetricDisplay;
}

export const WellnessChartTooltip = memo(({ row }: WellnessChartTooltipProps) => {
  const { question } = row;
  const scaleLabels = useMemo(
    () => ({
      low: question.leftlabel || 'Low',
      mid: 'Neutral',
      high: question.rightlabel || 'High',
    }),
    [question.leftlabel, question.rightlabel],
  );

  const preValue = row.pre ?? 0;
  const postValue = row.post ?? 0;
  const improvementPercentage = row.improvementPercentage;
  const hasImprovement = improvementPercentage !== null;
  const improvementColor = hasImprovement
    ? getImprovementColor(question.higherisbetter, improvementPercentage)
    : 'text-gray-600';
  const improvementDisplay = hasImprovement ? getImprovementDisplay(improvementPercentage) : null;

  return (
    <div className="relative z-[9999] w-[480px] rounded-md border border-[var(--anl-border)] bg-[var(--anl-surface-elev)] text-[var(--anl-text)] shadow-lg">
      <div className="border-b border-[var(--anl-border)] px-4 py-3">
        <h3 className="text-sm font-semibold">{row.metric}</h3>
        <p className="mt-1 text-base font-medium leading-relaxed text-[var(--anl-text)]">
          {question.label}
        </p>
        <div className="mt-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-[var(--anl-text-muted)]">
            Expected outcome
          </p>
          <p className="mt-1 text-sm leading-relaxed text-[var(--anl-text)]">
            We expect {row.metric} to go {question.higherisbetter ? 'UP' : 'DOWN'}
          </p>
        </div>
      </div>

      <div className="space-y-2 p-4">
        <div className="space-y-3">
          <ProgressBar
            label="Pre-Session"
            value={preValue}
            color="blue"
            scaleLabels={scaleLabels}
          />
          <ProgressBar
            label="Post-Session"
            value={postValue}
            color="green"
            scaleLabels={scaleLabels}
          />
        </div>

        {improvementDisplay && (
          <div className="rounded-md border-t border-[var(--anl-border)] bg-[var(--anl-surface)] p-3">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Overall Change</span>
              <div className={`text-lg font-bold ${improvementColor} flex items-center space-x-1`}>
                <span>
                  {improvementDisplay.sign}
                  {improvementDisplay.value}%
                </span>
                <span className="text-xs">{improvementDisplay.arrow}</span>
              </div>
            </div>
          </div>
        )}
        <p className="mt-1 text-xs italic text-[var(--anl-text-muted)]">
          Average results for this question
        </p>
      </div>
    </div>
  );
});
